"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useSignUp, useSignIn, ClerkFailed, ClerkLoading } from "@clerk/nextjs";
import { Loader } from "lucide-react";
import { trackEvent, logError } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { ArrowRightThin } from "@/components/icons/ArrowRightThin";
import { GoogleSignInButton } from "@/components/auth/GoogleSignInButton";
import { OtpInput } from "@/components/auth/OtpInput";
import { setReturnIntent, consumeReturnIntent } from "@/lib/auth/return-intent";
import { withChunkRetry, isChunkLoadError } from "@/lib/auth/chunk-retry";


type AuthCardProps = {
  onSuccess?: () => void;
  redirectTo?: string;
  skipRedirect?: boolean;
  /** Woher der Login kam — landet in den Fehlerprotokollen. Logins ueber
   *  Modale passieren auf beliebigen Seiten und waren nach URL nicht von
   *  Seiten-Logins zu unterscheiden. */
  source?: string;
};

/**
 * Zeigt einen "Seite neu laden"-Hinweis, wenn Clerk bis dahin nicht geladen
 * ist.
 *
 * Bemessen an Regular 2G (~250 kbit/s, ~300 ms Latenz): Das Clerk-Bundle
 * ist 322 KB, was dort allein rund 10 Sekunden Uebertragung bedeutet — plus
 * DNS, TLS und die Zeit, bis der Browser ueberhaupt damit anfaengt. 15s waren
 * dafuer zu knapp; der Nutzer bekam den Reload-Hinweis, waehrend das Bundle
 * noch unterwegs war, und ein Reload beginnt von vorn.
 *
 * 30s lassen dem Bundle das Dreifache seiner reinen Uebertragungszeit. Bleibt
 * es darueber hinaus aus, ist ein Reload die bessere Antwort als weiter zu
 * warten.
 */
const CLERK_LOAD_TIMEOUT_MS = 30000;

/**
 * Letzte Instanz gegen einen haengenden Clerk-Aufruf.
 *
 * Bewusst sehr grosszuegig. Die beiden Fehler sind nicht gleich teuer:
 *
 * - Zu frueh abgebrochen: Ein laufender, funktionierender Login wird
 *   verworfen. Der Nutzer tippt erneut, Clerk lehnt mit `session_exists` ab
 *   (83 Faelle in Production), und der Code aus der ersten Mail ist verbraucht.
 * - Zu spaet abgebrochen: Der Nutzer wartet laenger auf eine Meldung, die er
 *   ohnehin nur bekommt, wenn nichts mehr passiert.
 *
 * Der erste Fall kostet den Anmeldeversuch, der zweite nur Geduld.
 *
 * Bemessen an Regular 2G (~250 kbit/s, ~300 ms Latenz), wo der Login noch
 * funktionieren muss. Der E-Mail-Schritt macht bei einem bestehenden Konto
 * zwei Requests nacheinander — `signIn.create`, dann `prepareFirstFactor`
 * (bei einem neuen Konto kommt `signUp.create` dazu) — und jeder kann eine
 * Bundle-Nachladung samt Retry ausloesen (bis zu 3 Versuche mit 400/800 ms
 * Pause). Eine Minute deckt das im Normalfall ab.
 *
 * Ein Spinner laeuft dabei sichtbar weiter; der Nutzer sieht also, dass etwas
 * passiert.
 */
const STUCK_REQUEST_TIMEOUT_MS = 60000;

/**
 * Der Clerk-Aufruf, in dem der Login gerade steht.
 *
 * `signin_probe` und `signin_create` sind derselbe Aufruf, aber nicht
 * derselbe Vorgang: Weil Sign-in zuerst versucht wird, laeuft
 * `signIn.create` auch fuer Leute *ohne* Konto — dort ist es ein
 * Vorab-Check, der mit `form_identifier_not_found` scheitern *soll*, bevor
 * der Sign-up-Pfad beginnt. Ohne die Trennung waere ein haengender
 * Bestandsnutzer-Login von einer haengenden Neuregistrierung im
 * Vorab-Check nicht zu unterscheiden — beide traegen `signin_create`.
 *
 * Welcher der beiden Faelle vorliegt, steht erst fest, wenn der Aufruf
 * zurueckkommt. Deshalb beginnt jeder Versuch als `signin_probe` und wird
 * erst bei Erfolg zu `signin_create` korrigiert: Ein Haenger bleibt damit
 * als `signin_probe` stehen — ehrlich unentschieden statt falsch
 * zugeordnet.
 */
type LoginPhase =
  | "idle"
  | "signin_probe"
  | "signin_create"
  | "prepare_factor"
  | "signup_create"
  | "signup_prepare"
  | "verify_signin"
  | "verify_signup"
  | "set_active";

/** Gezeigt, wenn Clerk nicht nutzbar ist — erreicht ueber Clerks eigenen
 *  `status: "error"` oder ueber unseren Timeout, wenn es nie settled. */
const LoadFailure = () => (
  <>
    <h2 className="text-headline-xl text-black">
      Anmeldung konnte nicht geladen werden
    </h2>
    <p className="text-copy text-gray-75">
      Bitte prüfe deine Internetverbindung und lade die Seite neu.
    </p>
    <Button
      type="button"
      variant="black"
      size="lg"
      className="mt-2 w-full"
      onClick={() => window.location.reload()}
    >
      Seite neu laden
    </Button>
  </>
);

/** Shared card shell so the loading, error and ready states have identical chrome. */
const CardShell = ({ children }: { children: React.ReactNode }) => (
  <div className="flex w-full flex-col items-center gap-10 rounded-[24px] bg-white px-6 py-10 xl:w-[520px] xl:rounded-[32px] xl:px-10">
    {children}
  </div>
);

const AuthCard = ({ onSuccess, redirectTo, skipRedirect, source = "unknown" }: AuthCardProps) => {
  const { isLoaded: signUpLoaded, signUp, setActive: setActiveSignUp } = useSignUp();
  const { isLoaded: signInLoaded, signIn, setActive: setActiveSignIn } = useSignIn();
  // Seed sessionStorage from URL param (handles fresh-tab / direct link to /login?redirect=...)
  useEffect(() => {
    if (redirectTo) setReturnIntent(redirectTo, "login_param");
  }, [redirectTo]);

  const [emailAddress, setEmailAddress] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<"email" | "verify">("email");
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [loadTimedOut, setLoadTimedOut] = useState(false);
  /** Der Request laeuft ungewoehnlich lange. Kein Fehler — nur ein Hinweis
   *  neben dem weiterlaufenden Spinner, damit der Nutzer weiss, dass er
   *  warten oder neu laden kann. */
  const [takingLong, setTakingLong] = useState(false);
  /** Der Retry verlaengert das erneute Senden um bis zu ~1,2 s —
   *  ohne Sperre loesen zwei Klicks zwei Code-Mails aus. */
  const [resending, setResending] = useState(false);
  /** Macht Wiederholungen im Log lesbar: aus "8 Fehler in einer Minute"
   *  wird attempt: 1..8 desselben Nutzers. */
  const verifyAttempts = useRef(0);

  const isLoaded = signUpLoaded && signInLoaded;

  /**
   * Laeuft gerade eine Weiterleitung nach erfolgreichem Login?
   *
   * Dann bleibt der Button absichtlich gesperrt — ihn mitten in der Navigation
   * zu entsperren laedt zum zweiten Tippen ein, woraus der "already signed
   * in"-Fehler entsteht.
   *
   * Als Ref und nicht als lokale Variable, weil der Watchdog unten es sehen
   * muss: er kennt nur `loading` und wuerde einen erfolgreichen, aber langsamen
   * Login nach 25s mit einer Fehlermeldung ueberschreiben — also genau den
   * Doppelklick provozieren, den die Sperre verhindern soll.
   */
  const navigatingRef = useRef(false);

  /** Wann der Watchdog zuletzt angeschlagen hat. Kommt der Request danach
   *  doch noch durch, laesst sich daran messen, wie lange es wirklich
   *  gedauert hat — und dass der Nutzer nicht aufgegeben hat. */
  const stalledAt = useRef<number | null>(null);

  /**
   * Welcher Clerk-Aufruf gerade laeuft.
   *
   * Der Watchdog wusste bisher nur, *dass* der E-Mail-Schritt haengt, nicht
   * *wo*. In Production stehen 28 Haenger, alle mit `waited_ms` von exakt
   * 60.001 und `clerk_status: "ready"` — Clerk ist also geladen, der Aufruf
   * kehrt nur nie zurueck.
   *
   * `is_existing_user` beantwortet die Frage nicht: Das Feld ist ein State
   * mit Startwert `false`, der erst *nach* dem jeweiligen Aufruf gesetzt
   * wird. Wenn der Watchdog feuert, steht dort zwangslaeufig `false` —
   * unabhaengig davon, welcher Aufruf haengt. Die 28 Faelle liegen also
   * nicht deshalb im Sign-up-Pfad, weil dort `false` steht.
   *
   * Was die Phase leistet, und was nicht:
   *
   * - `signup_create` beweist Turnstile. Der Bot-Schutz haengt an genau
   *   diesem Aufruf, ein Haenger dort hat keine andere Erklaerung.
   * - `signin_create` schliesst Turnstile **nicht** aus. Clerk initialisiert
   *   das Widget bereits beim Rendern des `#clerk-captcha`-Knotens, also
   *   bevor irgendein Aufruf laeuft. Ein haengendes Widget kann die
   *   Netzwerk-Queue belasten, waehrend `signIn.create` laeuft.
   *
   * Die Aussage ist damit einseitig belastbar: Sie kann den Bot-Schutz
   * bestaetigen, aber nicht freisprechen. Fuer die Entscheidung ueber
   * Clerks "Smart"-Einstellung — die nicht rueckgaengig zu machen ist —
   * genuegt genau die Richtung, die sie belegt.
   *
   * Als Ref, nicht als State: Der Wert wird mitten im laufenden Request
   * gesetzt und darf kein Rendern ausloesen. Der Watchdog liest ihn beim
   * Feuern, also genuegt der aktuelle Stand.
   */
  const phase = useRef<LoginPhase>("idle");

  /**
   * Wann die aktuelle Phase begann.
   *
   * Ohne diesen Wert schreibt `waited_ms` die gesamte Wartezeit seit dem
   * Absenden der letzten Phase zu. Braucht `signin_probe` 55s und haengt
   * `signup_create` danach bei 5s, meldet der Watchdog 60s auf
   * `signup_create` — und laesst den eigentlichen Bremser unsichtbar.
   * `ms_in_phase` trennt beides.
   */
  const phaseStartedAt = useRef<number>(0);

  /** Die Phase im Moment des Haengers, festgehalten fuer `reportRecovery`.
   *  `phase` selbst wandert bis dahin weiter und taugt dort nicht mehr. */
  const stalledPhase = useRef<LoginPhase>("idle");

  /**
   * Setzt die Phase und den Startzeitpunkt gemeinsam.
   *
   * Als Funktion, damit die beiden nicht auseinanderlaufen koennen: Ein
   * vergessener Zeitstempel faellt nicht auf, er verfaelscht nur still
   * `ms_in_phase`.
   */
  const enterPhase = (next: LoginPhase) => {
    phase.current = next;
    phaseStartedAt.current = Date.now();
  };

  /**
   * Setzt die Diagnose-Marker auf Anfang.
   *
   * Als eine Funktion, weil die vier Werte zusammengehoeren und bisher an
   * drei Stellen einzeln zurueckgesetzt wurden — `stalledPhase` fehlte dort,
   * womit ein aufgegebener Versuch seine Phase in den naechsten mitnahm.
   * Sichtbar wurde das nicht, weil der Watchdog beide Marker gemeinsam neu
   * setzt; es war eine Falle fuer die naechste Aenderung, kein Fehler im
   * Ergebnis.
   */
  const resetStallMarkers = () => {
    stalledAt.current = null;
    stalledPhase.current = "idle";
    phase.current = "idle";
    phaseStartedAt.current = 0;
  };

  /**
   * Gemeinsamer Kontext fuer Login-Fehler. Ohne ihn ist eine Nutzeranfrage
   * nicht auffindbar: 722 der 829 Clerk-Fehler in `error_logs` haben keine
   * userId, weil beim Login noch keine Session existiert.
   *
   * Die E-Mail wird serverseitig in `/api/analytics/error` gehasht — sie
   * verlaesst den Browser im Klartext, landet aber nie so in der Datenbank.
   */
  const errorContext = (step: string, extra?: Record<string, unknown>) => {
    const nav =
      typeof navigator !== "undefined"
        ? (navigator as Navigator & {
            connection?: { effectiveType?: string; rtt?: number };
          })
        : undefined;
    // Belegt die Mobilfunk-These, statt sie zu erschliessen: effectiveType
    // ("4g"/"3g"/"2g"/"slow-2g") und rtt sagen, unter welchen Bedingungen der
    // Fehler auftrat. Nur in Chromium-Browsern vorhanden — in Firefox und
    // Safari bleiben die Felder null, was die Auswertung nicht stoert.
    const conn = nav?.connection;

    return {
      context: "auth",
      email: emailAddress,
      step,
      is_existing_user: isExistingUser,
      source,
      // Nur im Verify-Schritt aussagekraeftig; beim E-Mail-Schritt ist der
      // Zaehler noch 0.
      ...(step === "verify" ? { attempt: verifyAttempts.current } : {}),
      // Die mobile Haeufung war bisher nur erschlossen, nicht belegt.
      user_agent: nav?.userAgent ?? null,
      // Ohne diese Felder laesst sich "ging bei mir nicht" nicht einordnen:
      // war der Nutzer offline, im 2G-Netz, oder lag es an uns?
      online: nav?.onLine ?? null,
      network_type: conn?.effectiveType ?? null,
      network_rtt_ms: conn?.rtt ?? null,
      // Ein Wechsel Vordergrund/Hintergrund waehrend des Logins ist ein
      // bekannter Ausloeser fuer abgebrochene Requests auf Mobilgeraeten.
      page_visibility: typeof document !== "undefined" ? document.visibilityState : null,
      viewport:
        typeof window !== "undefined" ? `${window.innerWidth}x${window.innerHeight}` : null,
      page_url: typeof window !== "undefined" ? window.location.pathname : null,
      occurred_at: new Date().toISOString(),
      ...extra,
    };
  };

  /**
   * Haelt fest, dass in dieser Sitzung ein Login durchgelaufen ist.
   *
   * Ohne diesen Marker laesst sich beim spaeteren `session_exists` nicht
   * sagen, ob der Nutzer in derselben Sitzung schon einmal erfolgreich
   * angemeldet war — genau das trennt aber die beiden Erklaerungen.
   *
   * sessionStorage, nicht localStorage: die Frage gilt pro Browser-Sitzung.
   */
  /**
   * Meldet, dass ein zuvor stehengebliebener Schritt doch noch durchlief.
   *
   * Erst dieses Gegenstueck macht `login_stalled` auswertbar: Ohne es sieht
   * man nur, dass jemand lange wartete, nicht ob er ankam. Mit beiden
   * Ereignissen laesst sich die Frage beantworten, die zaehlt — scheitern
   * Nutzer auf bestimmten Geraeten oder Verbindungen dauerhaft, oder dauert
   * es dort nur laenger?
   */
  const reportRecovery = (schritt: string) => {
    if (stalledAt.current === null) return;
    trackEvent("login_recovered_after_stall", {
      step: schritt,
      // Die Phase aus dem Moment des Haengers, nicht die aktuelle: Bis
      // `reportRecovery` laeuft, ist der Ablauf laengst weitergewandert
      // (typisch bis `set_active`). Nur der festgehaltene Wert passt zum
      // `login_stalled` davor und beantwortet, welcher Aufruf langsam war.
      phase: stalledPhase.current,
      total_ms: Date.now() - stalledAt.current,
      surface: source,
    }, {
      // Zwei der vier Aufrufer liegen hinter `setActive` (Verify-Schritt),
      // zwei davor (E-Mail-Schritt, da existiert noch keine Session). Der
      // Aufruf deckt beides ab: Ohne Session liefert `activeUserId()` null,
      // also genau den bisherigen Zustand.
      userId: activeUserId(),
    });
    // Nur die Haenger-Marker loeschen, nicht `phase`/`phaseStartedAt`: Der
    // Ablauf laeuft hier weiter (nach `prepare_factor` folgt der Verify-
    // Schritt), und ein Reset mitten darin wuerde die naechste Phase ohne
    // Startzeit lassen.
    stalledAt.current = null;
    stalledPhase.current = "idle";
  };

  const markLoginSucceeded = () => {
    try {
      sessionStorage.setItem("dialetics_login_completed_at", new Date().toISOString());
    } catch {
      // Privater Modus o.ae. — dann fehlt nur ein Diagnosefeld.
    }
  };

  /**
   * Behandelt `session_exists`: Clerk lehnt den Login ab, weil bereits eine
   * Session besteht.
   *
   * Aus Nutzersicht ist das kein Fehler — er ist angemeldet, der Login war
   * also erfolgreich, nur eben schon vorher. Bis hierher zeigte die Karte
   * stattdessen eine Sackgasse, und zwar auf Englisch: Clerks `longMessage`
   * wurde ungefiltert durchgereicht, und die deDE-Lokalisierung gilt nur fuer
   * Clerks eigene UI-Komponenten, nicht fuer diese headless Karte.
   *
   * 83 Faelle in Production, davon 81 auf /login.
   */
  const handleAlreadySignedIn = () => {
    // skipRedirect-Aufrufer (PricingSection, MembershipPaymentModal)
    // navigieren nicht — sie starten den Checkout. `onSuccess` deckt genau
    // diesen Zustand ab: Der Nutzer ist angemeldet.
    if (skipRedirect) {
      onSuccess?.();
      return;
    }
    navigatingRef.current = true;
    // Ein wartender Intent hat Vorrang: Wer ueber einen gesperrten Artikel
    // kam, soll dorthin und nicht aufs Dashboard.
    const intent = consumeReturnIntent();
    window.location.href = intent?.url ?? "/dashboard";
  };

  /**
   * Zusatzangaben fuer `session_exists` — Clerk lehnt den Login ab, weil
   * bereits eine Session besteht.
   *
   * 83 Faelle in Production, davon 81 auf /login, gleichmaessig verteilt seit
   * Maerz (9-23 pro Monat, bei ~99 Logins im Juli). Der Nutzer war also
   * angemeldet und hat es trotzdem noch einmal versucht — das passt genau auf
   * den Bericht "loggt sich ein und bleibt auf der Startseite".
   *
   * Offen ist, welcher von zwei Faellen zutrifft:
   *
   *   A) Die Weiterleitung schlaegt fehl, der Nutzer bleibt auf /login.
   *   B) Die Weiterleitung gelingt, aber die Zielseite haelt ihn fuer
   *      ausgeloggt — er geht zurueck und versucht es erneut.
   *
   * Die Felder unten trennen die beiden: Bei A ist `referrer` leer oder
   * zeigt auf /login selbst und die Session ist frisch. Bei B zeigt
   * `referrer` auf die Zielseite.
   *
   * Nur bei diesem einen Fehlercode erhoben — `window.Clerk` anzufassen
   * kostet sonst unnoetig.
   */
  /**
   * Die Clerk-User-ID direkt nach `setActive`.
   *
   * `trackEvent` haengt sonst die `user_id` an, die `setAnalyticsUser` in
   * providers.tsx gesetzt hat — und die kommt aus `useAuth()`, also aus
   * React-State, der erst im naechsten Render ankommt. Zum Zeitpunkt von
   * `user_logged_in` ist sie deshalb immer noch `null`: In Production tragen
   * alle 720 `user_logged_in` und 338 `membership_signup_completed` keine
   * `user_id`.
   *
   * `window.Clerk.user` umgeht React: Clerk aktualisiert sein eigenes Objekt
   * in `setActive`, also bevor der Effect laeuft, der `_userId` setzt.
   *
   * Belastbar ist das bis zu einem Punkt. `sessionExistsContext` unten liest
   * `Clerk.user` schon heute — aber in einer *bestehenden* Session, nicht
   * direkt nach `setActive`; als Beleg taugt das nur bedingt (5 Faelle in
   * Production, alle mit `clerk_has_user: true`). Bleibt die ID wider
   * Erwarten aus, faellt das Ereignis auf `null` zurueck — also auf genau
   * den Zustand von heute, nicht auf etwas Schlechteres. Ob der Fix greift,
   * zeigt nach dem Deploy der Anteil der Ereignisse mit `user_id`.
   */
  const activeUserId = (): string | null => {
    const clerk = (window as unknown as { Clerk?: { user?: { id?: string } } })
      .Clerk;
    return clerk?.user?.id ?? null;
  };

  const sessionExistsContext = () => {
    const clerk = (window as unknown as {
      Clerk?: {
        session?: { id?: string; status?: string; lastActiveAt?: Date };
        user?: { id?: string };
      };
    }).Clerk;

    const session = clerk?.session;
    const lastActive = session?.lastActiveAt;

    return {
      // Woher kam der Nutzer? Der aussagekraeftigste Wert: zeigt er auf die
      // Zielseite, wurde weitergeleitet und der Nutzer kam zurueck (Fall B).
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
      clerk_session_id: session?.id ?? null,
      clerk_session_status: session?.status ?? null,
      clerk_has_user: clerk?.user?.id ? true : false,
      // Wie alt ist die Session? Wenige Sekunden heisst: gerade erst
      // eingeloggt, die Weiterleitung ist unmittelbar davor gescheitert.
      session_age_ms:
        lastActive instanceof Date ? Date.now() - lastActive.getTime() : null,
      // Hat dieselbe Sitzung schon einen erfolgreichen Login gesehen? Wird in
      // handleVerify gesetzt, sobald setActive durch ist.
      login_completed_at: (() => {
        try {
          return sessionStorage.getItem("dialetics_login_completed_at");
        } catch {
          return null;
        }
      })(),
    };
  };

  /**
   * Macht Bundle-Haenger messbar — auch die, die der Retry auffaengt.
   *
   * `error_logs` sieht nur, was endgueltig scheitert. Ein Login, der erst im
   * zweiten Anlauf durchging, waere dort von einem reibungslosen nicht zu
   * unterscheiden. Nach dem Deploy liesse sich damit zwar sehen, dass niemand
   * mehr scheitert, aber nicht, ob das Bundle weiterhin bei jedem dritten
   * Mobilnutzer hakt — und ob es besser oder schlechter wird.
   *
   * Deshalb `trackEvent` (schreibt immer nach `analytics_events`) statt
   * `logError`: ein geretteter Login ist kein Fehler, sondern eine Kennzahl.
   */
  const trackRetryOutcome = ({
    recovered,
    attempts,
    durationMs,
    label,
    chunkUrl,
  }: {
    recovered: boolean;
    attempts: number;
    durationMs: number;
    label: string;
    chunkUrl: string;
  }) => {
    trackEvent(
      recovered ? "login_chunk_retry_recovered" : "login_chunk_retry_failed",
      {
        step: label,
        attempts,
        duration_ms: durationMs,
        // Kommt vom Aufrufer, nicht aus dem Pfad: ein Modal-Login passiert auf
        // beliebigen Seiten und waere ueber `location.pathname` nicht von
        // einem Seiten-Login zu unterscheiden.
        surface: source,
        chunk_url: chunkUrl,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      },
    );
  };

  // If Clerk never finishes loading (flaky mobile connection, blocked bundle),
  // stop showing an endless spinner and offer an explicit reload instead.
  useEffect(() => {
    if (isLoaded) {
      setLoadTimedOut(false);
      return;
    }
    const timer = setTimeout(() => {
      setLoadTimedOut(true);

      // Haelt fest, WARUM aufgegeben wurde — das fehlte bisher komplett.
      //   "error"   → clerk-js ist wirklich nicht geladen
      //   "ready"   → Clerk laeuft, nur die Hooks melden isLoaded nicht:
      //               ein React-Problem, kein Netzwerkproblem
      //   undefined → das Bundle wurde nie ausgefuehrt
      const clerk = (window as unknown as {
        Clerk?: { status?: string; loaded?: boolean; version?: string };
      }).Clerk;
      logError("Clerk load timeout", {
        ...errorContext("clerk_load", {
          clerk_status: clerk?.status ?? "undefined",
          clerk_loaded: clerk?.loaded ?? false,
          clerk_version: clerk?.version ?? null,
          signup_loaded: signUpLoaded,
          signin_loaded: signInLoaded,
        }),
      });
    }, CLERK_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, signUpLoaded, signInLoaded]);

  /**
   * Faengt den Fall, den `try/catch` nicht sehen kann: ein Clerk-Aufruf, der
   * weder erfuellt noch abgelehnt wird.
   *
   * Getestet: blockiert man die Clerk-API, kommt sofort eine Fehlermeldung und
   * der Button dreht sich gar nicht — `catch` und `finally` greifen also.
   * Nutzer berichten trotzdem von einem endlos drehenden Button, und
   * `clerk.status` ist dabei `"ready"`. Ein abgelehnter Request kann es
   * deshalb nicht sein; ein *haengender* schon: er scheitert nie, also feuert
   * auch das `finally` nie und `loading` bleibt fuer immer true.
   *
   * Der Timeout oben hilft hier nicht — er haengt an `!isLoaded` und greift
   * nur, solange Clerk gar nicht geladen ist. Sobald das E-Mail-Feld sichtbar
   * ist, war bisher niemand mehr zustaendig.
   *
   * 25s bewusst grosszuegig: auf GPRS darf ein langsamer, aber funktionierender
   * Login nicht abgebrochen werden. Letzte Instanz, nicht erste.
   */
  useEffect(() => {
    if (!loading) {
      setTakingLong(false);
      return;
    }

    const startedAt = Date.now();
    const timer = setTimeout(() => {
      // Laeuft eine Weiterleitung, ist `loading` absichtlich noch true — der
      // Login war erfolgreich. Hier einzugreifen wuerde den Button mitten in
      // der Navigation entsperren und eine Fehlermeldung ueber einen
      // funktionierenden Login legen.
      if (navigatingRef.current) return;

      const clerk = (window as unknown as {
        Clerk?: { status?: string; loaded?: boolean; version?: string };
      }).Clerk;

      // Einmal messen, zweimal melden: Zwei `Date.now()`-Aufrufe waeren zwei
      // minimal verschiedene Zahlen fuer dieselbe Groesse und liessen den
      // Log-Eintrag und das Event auseinanderlaufen.
      const firedAt = Date.now();
      const waitedMs = firedAt - startedAt;
      const msInPhase = phaseStartedAt.current
        ? firedAt - phaseStartedAt.current
        : null;

      logError("Clerk request never settled", {
        ...errorContext(step === "verify" ? "verify" : "email_submit", {
          watchdog: true,
          waited_ms: waitedMs,
          clerk_status: clerk?.status ?? "undefined",
          clerk_loaded: clerk?.loaded ?? false,
          clerk_version: clerk?.version ?? null,
          // Der eigentliche Zweck dieses Eintrags: Ohne die Phase ist nicht
          // unterscheidbar, ob der Bot-Schutz haengt (`signup_create`) oder
          // etwas anderes.
          phase: phase.current,
          // `waited_ms` misst seit dem Absenden, deckt also alle Phasen ab.
          // Erst die Differenz zeigt, ob diese Phase der Bremser war oder
          // nur die letzte in einer insgesamt langsamen Kette.
          ms_in_phase: msInPhase,
        }),
      });

      // Ohne dieses Ereignis endet die Spur hier. Der Watchdog feuert genau
      // einmal; ob der Nutzer danach doch noch durchkam oder aufgab, war
      // nicht erkennbar. Zusammen mit `login_recovered_after_stall` unten
      // laesst sich beides auseinanderhalten — und beantworten, ob Nutzer auf
      // bestimmten Geraeten dauerhaft scheitern.
      trackEvent("login_stalled", {
        step: step === "verify" ? "verify" : "email_submit",
        phase: phase.current,
        ms_in_phase: msInPhase,
        waited_ms: waitedMs,
        surface: source,
        clerk_status: clerk?.status ?? "undefined",
        network_type:
          (navigator as Navigator & { connection?: { effectiveType?: string } })
            .connection?.effectiveType ?? null,
        user_agent: navigator.userAgent,
      });
      stalledAt.current = startedAt;
      stalledPhase.current = phase.current;

      // Bewusst ohne Eingriff in den Zustand.
      //
      // Der Watchdog kann einen Clerk-Aufruf nicht abbrechen — er setzt nur
      // React-State. Der Request laeuft also weiter, und auf einer langsamen
      // Verbindung geht er danach regelmaessig noch durch. Ihn hier fuer
      // gescheitert zu erklaeren war deshalb doppelt falsch: Die
      // Fehlermeldung stand ueber einem funktionierenden Login, und das
      // Entsperren des Buttons lud zu einem zweiten Versuch ein — woraus
      // `session_exists` entsteht und der Code aus der ersten Mail verfaellt.
      //
      // Ein Zeitmesser kann nicht unterscheiden, ob ein Request haengt oder
      // nur langsam ist. Also entscheidet er es auch nicht mehr, sondern
      // protokolliert nur. Gegen einen wirklich haengenden Aufruf hilft der
      // Hinweis unten, der nach derselben Zeit neben dem Spinner erscheint
      // und das Neuladen anbietet — die Entscheidung trifft der Nutzer.
      setTakingLong(true);
    }, STUCK_REQUEST_TIMEOUT_MS);

    return () => clearTimeout(timer);
    // `step` bewusst nicht in den Deps: ein Schrittwechsel waehrend eines
    // laufenden Requests soll den Watchdog nicht neu starten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddress);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded) return;

    setError("");
    setLoading(true);
    // Dieser Schritt navigiert nie. Trotzdem zuruecksetzen, damit ein Ref aus
    // einem frueheren Verify-Versuch den Watchdog nicht stumm schaltet.
    navigatingRef.current = false;
    // Ebenso die Stillstands-Marker: Blieben sie von einem gescheiterten
    // Versuch stehen, meldete der naechste Erfolg eine `total_ms`, die bis in
    // einen fremden Versuch zurueckreicht — und eine Phase, die auf den
    // falschen Aufruf zeigt.
    resetStallMarkers();

    try {
      // Sign-in zuerst, Sign-up nur wenn es das Konto nicht gibt.
      //
      // Das ist Clerks empfohlene Reihenfolge fuer einen kombinierten
      // "sign-in-or-up"-Ablauf — und sie loest ein konkretes Problem: Clerks
      // Bot-Schutz (Cloudflare Turnstile) haengt nur an `signUp.create`. Wer
      // ein Konto hat, musste bisher trotzdem durch die CAPTCHA-Pruefung,
      // weil Sign-up zuerst versucht wurde.
      //
      // Auf GPRS scheitert Turnstile: Requests von 16s, 28s und 73s, dann
      // "Widget seem to have hung" (300030) und ein 400 mit `captcha_invalid`.
      // Damit war der Login fuer Bestandsnutzer unmoeglich, obwohl sie den
      // Bot-Schutz gar nicht braeuchten. In drei Monaten stehen 307 Logins
      // gegen 157 Registrierungen — es trifft also die Mehrheit.
      //
      // Preis: Fuer neue Nutzer kommt ein fehlschlagender Request dazu.
      // Bei diesem Verhaeltnis die richtige Seite zum Optimieren.
      // Noch unentschieden: Fuer Bestandsnutzer ist das der Login, fuer alle
      // anderen der Vorab-Check, der gleich scheitert. Erst die Antwort sagt,
      // welcher Fall vorlag — haengt der Aufruf, bleibt es `signin_probe`.
      enterPhase("signin_probe");
      const si = await signIn.create({ identifier: emailAddress });
      // Der Aufruf kam durch, es gibt also ein Konto: rueckwirkend praezisiert.
      // Bewusst ohne `enterPhase` — es ist derselbe, bereits beendete Aufruf,
      // nur nachtraeglich richtig benannt. Die Uhr hier neu zu starten wuerde
      // die naechste `ms_in_phase` um die Dauer dieses Aufrufs kuerzen.
      phase.current = "signin_create";
      const emailFactor = si.supportedFirstFactors?.find(
        (f: any) => f.strategy === "email_code"
      );
      if (emailFactor && "emailAddressId" in emailFactor) {
        enterPhase("prepare_factor");
        await signIn.prepareFirstFactor({
          strategy: "email_code",
          emailAddressId: emailFactor.emailAddressId,
        });
        setIsExistingUser(true);
        reportRecovery("email_submit");
        // Der Schritt hat funktioniert — eine Meldung aus dem vorigen Versuch
        // (etwa der Watchdog, der waehrend eines langsamen Requests gefeuert
        // hat) darf nicht mit in den Verify-Schritt wandern.
        setError("");
        setStep("verify");
      } else {
        // Clerk bietet fuer diesen Account keinen E-Mail-Code an. Der Nutzer
        // kann sich damit ueberhaupt nicht anmelden — genau der Fall, der als
        // Supportanfrage ankommt und bisher nirgends stand.
        logError("No email_code factor available for account", {
          ...errorContext("email_submit", {
            available_factors:
              si.supportedFirstFactors?.map((f: any) => f.strategy) ?? [],
          }),
        });
        setError(
          "E-Mail-Code-Anmeldung ist nicht verfügbar. Bitte kontaktiere den Support."
        );
      }
    } catch (err: any) {
      const errorCode = err.errors?.[0]?.code;

      // Kein Konto unter dieser Adresse — also registrieren. Hier greift der
      // Bot-Schutz, und hier gehoert er auch hin.
      if (errorCode === "form_identifier_not_found") {
        try {
          // Ab hier haengt Clerks Bot-Schutz im Aufruf. Bleibt der Watchdog
          // spaeter in dieser Phase stehen, ist Turnstile die Ursache.
          enterPhase("signup_create");
          await signUp.create({ emailAddress });
          enterPhase("signup_prepare");
          await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
          setIsExistingUser(false);
          reportRecovery("email_submit");
          setError("");
          setStep("verify");
        } catch (signUpErr: any) {
          const signUpCode = signUpErr.errors?.[0]?.code;
          logError(
            signUpErr,
            errorContext("email_submit_signup", {
              clerk_error_code: signUpCode ?? null,
              ...(signUpCode === "session_exists" ? sessionExistsContext() : {}),
            }),
          );

          if (signUpCode === "session_exists") {
            handleAlreadySignedIn();
            return;
          }

          if (signUpCode === "captcha_invalid") {
            // Clerks Bot-Schutz hat es nicht rechtzeitig geladen. Sein
            // eigener Text ist englisch und raet zu Browserwechsel oder
            // Deaktivieren von Erweiterungen — beides trifft den haeufigsten
            // Fall nicht. Bei langsamer Verbindung hilft ein zweiter Versuch,
            // weil das Widget dann meist im Cache liegt.
            setError(
              "Die Sicherheitsprüfung konnte nicht geladen werden. Das passiert " +
                "bei langsamer Verbindung — bitte versuche es gleich noch einmal.",
            );
            return;
          }

          setError(
            signUpErr.errors?.[0]?.longMessage ||
              "Registrierung fehlgeschlagen. Bitte versuche es erneut."
          );
        }
        return;
      }

      logError(
        err,
        errorContext("email_submit", {
          clerk_error_code: errorCode ?? null,
          ...(errorCode === "session_exists" ? sessionExistsContext() : {}),
        }),
      );

      if (errorCode === "session_exists") {
        handleAlreadySignedIn();
        return;
      }

      setError(
        err.errors?.[0]?.longMessage ||
          "Anmeldung fehlgeschlagen. Bitte versuche es erneut."
      );
    } finally {
      // Nicht entsperren, wenn eine Weiterleitung laeuft: Der Button waere
      // mitten in der Navigation wieder klickbar, und genau daraus entsteht
      // der zweite Versuch.
      if (!navigatingRef.current) setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded) return;

    setError("");
    setLoading(true);
    verifyAttempts.current += 1;
    navigatingRef.current = false;
    // Siehe handleEmailSubmit: stehengebliebene Marker verfaelschen
    // `total_ms` des naechsten Erfolgs und zeigen auf den falschen Aufruf.
    resetStallMarkers();

    try {
      if (isExistingUser) {
        // withChunkRetry nur um den Clerk-Aufruf: schlaegt eine Bundle-
        // Nachladung fehl, hilft ein zweiter Versuch. Bei fachlichen Fehlern
        // (falscher Code, bereits angemeldet) wird nicht wiederholt.
        enterPhase("verify_signin");
        const result = await withChunkRetry(
          () => signIn.attemptFirstFactor({ strategy: "email_code", code }),
          { label: "verify/sign-in", onRetryOutcome: trackRetryOutcome },
        );
        if (result.status === "complete") {
          // Das Ziel bekommt Clerk mit, statt danach selbst zu navigieren.
          //
          // Belegt durch zwei Tests am 29.08.: Steht die
          // FALLBACK_REDIRECT_URL auf /onboarding/membership, funktioniert der
          // Login und der Onboarding-Weg bricht; steht sie auf /dashboard,
          // dreht es sich um. Der Nutzer landet also immer dort, wohin Clerk
          // zeigt — nie dort, wohin unser `router.push` wollte. Der wurde
          // jedes Mal ueberschrieben.
          //
          // Das erklaert die vier gescheiterten Diagnosen: Die Navigation ist
          // nie haengengeblieben, sie ging nur woandershin. Deshalb hat auch
          // der 8s-Watchdog nie ausgeloest.
          //
          // Im skipRedirect-Fall bewusst OHNE redirectUrl: PricingSection und
          // MembershipPaymentModal duerfen nicht navigieren, sie starten
          // direkt den Stripe-Checkout.
          // `setActive` haelt `loading` bis zur Navigation — haengt es, sieht
          // der Nutzer denselben Spinner wie bei einem haengenden Request.
          enterPhase("set_active");
          await setActiveSignIn({
            session: result.createdSessionId,
            ...(skipRedirect ? {} : { redirectUrl: "/onboarding/membership" }),
          });
          reportRecovery("verify");
          markLoginSucceeded();
          // `userId` explizit mitgeben: Der Wert aus `setAnalyticsUser`
          // haengt an React-State und ist hier noch `null` (siehe
          // `activeUserId`).
          trackEvent("user_logged_in", undefined, { userId: activeUserId() });

          if (skipRedirect) {
            // Modal-Aufrufer (PricingSection, MembershipPaymentModal) machen
            // selbst weiter — Checkout starten statt navigieren.
            onSuccess?.();
          } else {
            // Kein Navigationsaufruf: Das Ziel steckt im `redirectUrl` oben,
            // Clerk navigiert selbst. Ein zusaetzlicher router.push wuerde
            // ueberschrieben — genau daran ist die Weiterleitung zuvor
            // gescheitert.
            navigatingRef.current = true;
            return;
          }
        } else {
          // Fehlte bisher: bei einem anderen Status passierte im Login-Zweig
          // gar nichts — keine Meldung, keine Navigation, nur der Spinner.
          // Ohne result.status laesst sich Fehler A nicht von Fehler B trennen.
          logError(
            `Sign-in incomplete: ${result.status}`,
            errorContext("verify_incomplete", { clerk_status: result.status }),
          );
          setError(
            `Verifizierung unvollständig (Status: ${result.status}). Bitte versuche es erneut.`
          );
        }
      } else {
        enterPhase("verify_signup");
        const result = await withChunkRetry(
          () => signUp.attemptEmailAddressVerification({ code }),
          { label: "verify/sign-up", onRetryOutcome: trackRetryOutcome },
        );
        if (result.status === "complete") {
          // Siehe Sign-in-Zweig: Clerk navigiert, nicht wir — und ein
          // haengendes `setActive` sieht aus wie ein haengender Request.
          enterPhase("set_active");
          await setActiveSignUp({
            session: result.createdSessionId,
            ...(skipRedirect
              ? {}
              : { redirectUrl: "/onboarding/membership?initial=true" }),
          });
          reportRecovery("verify");
          markLoginSucceeded();
          // Bei der Registrierung liefert Clerk die ID im Ergebnis selbst —
          // direkter als der Umweg ueber `window.Clerk`, mit demselben
          // Fallback, falls sie ausbleibt.
          trackEvent("membership_signup_completed", undefined, {
            userId: result.createdUserId ?? activeUserId(),
          });

          if (skipRedirect) {
            onSuccess?.();
          } else {
            // Siehe Sign-in-Zweig: Clerk navigiert ueber `redirectUrl`.
            navigatingRef.current = true;
            return;
          }
        } else {
          logError(
            `Sign-up incomplete: ${result.status}`,
            errorContext("verify_incomplete", { clerk_status: result.status }),
          );
          setError(
            `Verifizierung unvollständig (Status: ${result.status}). Bitte versuche es erneut.`
          );
        }
      }
    } catch (err: any) {
      const code = err.errors?.[0]?.code;
      // Falscher oder abgelaufener Code sind normale Nutzerfehler, keine
      // Stoerungen. error_logs hat bereits 13.858 Eintraege in 30 Tagen —
      // sie hier mitzuschreiben wuerde die Auswertung verrauschen, fuer die
      // dieses Logging gebaut wurde.
      const isUserMistake =
        code === "form_code_incorrect" || code === "verification_expired";
      if (!isUserMistake) {
        logError(err, errorContext("verify", { clerk_error_code: code ?? null }));
      }
      if (code === "form_code_incorrect") {
        setError("Der Code ist falsch. Bitte versuche es erneut.");
      } else if (code === "verification_expired") {
        setError("Der Code ist abgelaufen. Bitte fordere einen neuen an.");
      } else if (isChunkLoadError(err)) {
        // Clerk laedt Teile seines Bundles erst beim Verify nach. Schlaegt das
        // fehl, ist die Datei nicht weg, sondern kam nicht rechtzeitig an.
        console.error(
          "[chunk-retry] Verify abgebrochen: Clerk-Bundle nicht ladbar, " +
            "Nutzer sieht die Verbindungs-Fehlermeldung.",
          err,
        );
        setError(
          "Verbindungsproblem beim Laden. Bitte versuche es noch einmal."
        );
      } else {
        setError("Verifizierung fehlgeschlagen. Bitte versuche es erneut.");
      }
    } finally {
      // Nur entsperren, wenn wir hier bleiben.
      if (!navigatingRef.current) setLoading(false);
    }
  };

  /**
   * Bewusst ohne `enterPhase`: Dieser Pfad laeuft ueber `resending`, nicht
   * ueber `loading`, und wird vom Watchdog gar nicht beobachtet. Eine Phase
   * zu setzen wuerde nur die des laufenden Verify-Versuchs ueberschreiben,
   * ohne dass sie je jemand liest.
   *
   * Die Marker bleiben deshalb unberuehrt — mit einer bekannten Folge: Haengt
   * ein Verify-Versuch, feuert der Watchdog, der Nutzer fordert einen neuen
   * Code an und *dann* loest der urspruengliche Aufruf doch noch aus, enthaelt
   * `total_ms` die Wartezeit auf den Resend mit. Das ist korrekt als
   * Wanduhrzeit ("so lange hat der Nutzer gewartet"), aber nicht als Dauer
   * eines einzelnen Aufrufs lesbar. Fuer die Frage, die dieses Logging
   * beantworten soll — welcher Aufruf haengt —, zaehlt `phase`/`ms_in_phase`
   * aus dem `login_stalled`, und das steht vor dem Resend fest.
   */
  const handleResendCode = async () => {
    if (!isLoaded || resending) return;

    setResending(true);
    setError("");
    try {
      // Gleiche Behandlung wie im Verify-Schritt: Dies ist der Weg, den ein
      // Nutzer nach "Code abgelaufen" nimmt, und er kann an denselben
      // Bundle-Nachladungen scheitern.
      if (isExistingUser) {
        const si = await withChunkRetry(
          () => signIn.create({ identifier: emailAddress }),
          { label: "resend/create", onRetryOutcome: trackRetryOutcome },
        );
        const emailFactor = si.supportedFirstFactors?.find(
          (f: any) => f.strategy === "email_code"
        );
        if (emailFactor && "emailAddressId" in emailFactor) {
          await withChunkRetry(
            () =>
              signIn.prepareFirstFactor({
                strategy: "email_code",
                emailAddressId: emailFactor.emailAddressId,
              }),
            { label: "resend/prepare", onRetryOutcome: trackRetryOutcome },
          );
        }
      } else {
        await withChunkRetry(
          () => signUp.prepareEmailAddressVerification({ strategy: "email_code" }),
          { label: "resend/sign-up", onRetryOutcome: trackRetryOutcome },
        );
      }
    } catch (err: any) {
      logError(
        err,
        errorContext("resend_code", {
          clerk_error_code: err.errors?.[0]?.code ?? null,
        }),
      );
      setError(
        isChunkLoadError(err)
          ? "Verbindungsproblem beim Laden. Bitte versuche es noch einmal."
          : "Code konnte nicht erneut gesendet werden.",
      );
    } finally {
      setResending(false);
    }
  };

  // Clerk not ready yet: never show interactive fields, because submitting
  // before the SDK is loaded would silently do nothing.
  if (!isLoaded) {
    return (
      <CardShell>
        <div className="flex w-full flex-col items-center gap-4 text-center">
          {/* Zwei Wege hierher, eine Meldung.
              <ClerkFailed> greift, sobald clerk-js `status: "error"` meldet —
              ein echter Fehler, ohne Raten und ohne Wartezeit. Der 15s-Timeout
              bleibt fuer den Fall, den Clerk nicht meldet: ewiges Haengen in
              "loading", ohne je zu scheitern. */}
          <ClerkFailed>
            <LoadFailure />
          </ClerkFailed>

          {/* Der Spinner bleibt, auch nach dem Timeout.
              Auf GPRS ist das Bundle (322 KB) nach 30s womoeglich noch
              unterwegs. Das Formular durch "Anmeldung konnte nicht geladen
              werden" zu ersetzen erklaerte eine langsame Verbindung fuer
              gescheitert — und der angebotene Reload beginnt von vorn.

              Stattdessen kommt der Hinweis unter den weiterlaufenden Spinner:
              Der Nutzer sieht, dass es weitergeht, und kann selbst neu laden.
              Meldet Clerk einen echten Fehler, greift <ClerkFailed> oben. */}
          <ClerkLoading>
            <Loader
              className="size-6 animate-spin text-gray-75"
              aria-hidden="true"
            />
            <p className="text-copy text-gray-75" aria-live="polite">
              Anmeldung wird vorbereitet...
            </p>
            {loadTimedOut && (
              <p className="text-copy-sm text-gray-75" aria-live="polite">
                Das dauert bei langsamer Verbindung länger. Bitte warte noch
                einen Moment — oder{" "}
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="underline hover:opacity-70"
                >
                  lade die Seite neu
                </button>
                .
              </p>
            )}
          </ClerkLoading>
        </div>
      </CardShell>
    );
  }

  if (step === "verify") {
    return (
      <CardShell>
        <div className="flex w-full flex-col gap-2 text-center">
          <h2 className="text-headline-xl text-black">
            Bestätige deine E-Mail-Adresse
          </h2>
          <p className="text-copy text-gray-75">
            Wir haben dir einen sechsstelligen Bestätigungscode an{" "}
            {emailAddress} geschickt. Bitte gib ihn unten ein, um deine
            E-Mail-Adresse zu bestätigen.
          </p>
        </div>

        <div className="flex w-full flex-col items-center gap-6">
          <form onSubmit={handleVerify} className="flex w-full flex-col gap-2">
            <OtpInput
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Bestätigungscode eingeben"
            />

            {takingLong && !error && (
              <p className="text-copy-sm text-center text-gray-75" aria-live="polite">
                Das dauert gerade länger. Bitte warte noch einen Moment — oder{" "}
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="underline hover:opacity-70"
                >
                  lade die Seite neu
                </button>
                .
              </p>
            )}

            {error && (
              <p className="text-copy-sm text-center [color:hsl(var(--red-100))]" aria-live="polite">
                {error}
              </p>
            )}

            <Button
              type="submit"
              variant="black"
              size="lg"
              className="mt-4 w-full"
              disabled={!isLoaded || loading || code.length < 6}
            >
              {loading ? (
                <Loader className="size-5 animate-spin" />
              ) : (
                <>
                  Bestätigen
                  <ArrowRightThin className="size-5" />
                </>
              )}
            </Button>
          </form>

          <p className="text-copy-sm-snug text-center text-gray-75">
            Hast du keinen Code erhalten?{" "}
            <button
              type="button"
              onClick={handleResendCode}
              disabled={resending}
              className="hover:underline disabled:opacity-50"
            >
              Code erneut senden.
            </button>
          </p>
        </div>
      </CardShell>
    );
  }

  return (
    <CardShell>
      <div className="flex w-full flex-col gap-2 text-center">
        <h2 className="text-headline-xl text-black">
          Dein Dialetics Account
        </h2>
      </div>

      <div className="flex w-full flex-col items-center gap-6">
        <GoogleSignInButton
          onBeforeRedirect={skipRedirect ? () => {
            setReturnIntent(window.location.pathname + window.location.search, "google_oauth");
            try {
              const url = new URL(window.location.href);
              sessionStorage.setItem("checkout_resume", JSON.stringify({
                path: window.location.pathname,
                ts: Date.now(),
                utm_source: url.searchParams.get("utm_source"),
                utm_campaign: url.searchParams.get("utm_campaign"),
                utm_content: url.searchParams.get("utm_content"),
                utm_medium: url.searchParams.get("utm_medium"),
              }));
            } catch {}
          } : undefined}
        />

        <div className="flex w-full items-center gap-3 px-6">
          <div className="h-px flex-1 bg-gray-10" />
          <span className="text-copy-sm-snug text-gray-50">oder</span>
          <div className="h-px flex-1 bg-gray-10" />
        </div>

        <form onSubmit={handleEmailSubmit} className="flex w-full flex-col gap-2">
          <input
            type="email"
            value={emailAddress}
            onChange={(e) => { setEmailAddress(e.target.value); setError(""); }}
            onBlur={() => setEmailTouched(true)}
            placeholder="Deine E-Mail"
            required
            className="h-[46px] w-full rounded-full bg-sand-10 border border-sand-10 px-6 py-4 text-lg text-black caret-sky-50 placeholder:text-gray-75 focus:outline-none focus:ring-2 focus:ring-sky-50 xl:text-xl"
          />

          {emailTouched && emailAddress && !isValidEmail && (
            <p className="text-copy-sm text-center [color:hsl(var(--red-100))]" aria-live="polite">
              Bitte gib eine gültige E-Mail-Adresse ein.
            </p>
          )}

          {takingLong && !error && (
            <p className="text-copy-sm text-center text-gray-75" aria-live="polite">
              Das dauert gerade länger. Bitte warte noch einen Moment — oder{" "}
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="underline hover:opacity-70"
              >
                lade die Seite neu
              </button>
              .
            </p>
          )}

          {error && (
            <p className="text-copy-sm text-center [color:hsl(var(--red-100))]" aria-live="polite">
              {error}
            </p>
          )}

          {/* Clerks Bot-Schutz (Cloudflare Turnstile) braucht diesen Knoten,
              bevor `signUp.create` laeuft. Fehlt er, faellt Clerk still auf
              ein unsichtbares Widget zurueck — und wenn das nicht laedt,
              scheitert die Registrierung mit `captcha_invalid`, ohne dass der
              Nutzer etwas tun kann.

              Genau das passierte auf GPRS: Turnstile-Requests von 16s, 16s
              und 65s, dann "Widget seem to have hung" (Fehler 300030) und ein
              400 auf /v1/client/sign_ups.

              Sichtbar gerendert bekommt der Nutzer im Zweifel eine
              Interaktionsmoeglichkeit statt eines stillen Fehlschlags. */}
          <div id="clerk-captcha" data-cl-size="flexible" className="empty:hidden" />

          <Button
            type="submit"
            variant="black"
            size="lg"
            className="mt-4 w-full"
            disabled={!isLoaded || loading || !isValidEmail}
          >
            {loading ? (
              <Loader className="size-5 animate-spin" />
            ) : (
              <>
                Anmelden
                <ArrowRightThin className="size-5" />
              </>
            )}
          </Button>
        </form>

        <p className="text-copy-sm-snug text-center text-gray-75">
          Mit der Registrierung akzeptierst du unsere{" "}
          <Link href="/agb" className="hover:underline">
            AGB
          </Link>{" "}
          und stimmst zu, dass wir deine Daten zur Bereitstellung und
          Verwaltung deines Kontos verarbeiten. Weitere Informationen findest du
          in unserer{" "}
          <Link href="/datenschutz" className="hover:underline">
            Datenschutzerklärung
          </Link>
          .
        </p>
      </div>
    </CardShell>
  );
};

export { AuthCard };
